import { useState, useMemo, memo } from 'react';
import { ChevronDown, ChevronRight, MessageSquare, User, Settings, LogOut, Users } from 'lucide-react';
import { AIMWindow } from './AIMWindow';
import { RunningManIcon, BuddyIcon } from './RunningManIcon';
import { StatusSelector, StatusDisplay } from './StatusSelector';
import { useDMContext } from '@/hooks/useDMContext';
import { useAuthor } from '@/hooks/useAuthor';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useLoginActions } from '@/hooks/useLoginActions';
import { genUserName } from '@/lib/genUserName';
import { cn } from '@/lib/utils';
import type { ConversationSummary } from '@/contexts/DMContext';

interface BuddyListProps {
  onSelectBuddy: (pubkey: string) => void;
  selectedPubkey: string | null;
  onNewIM?: () => void;
}

const BuddyItemComponent = ({
  pubkey,
  isSelected,
  onClick,
  lastActivity,
}: {
  pubkey: string;
  isSelected: boolean;
  onClick: () => void;
  lastActivity: number;
}) => {
  const author = useAuthor(pubkey);
  const metadata = author.data?.metadata;
  const displayName = metadata?.name || genUserName(pubkey);

  // Determine status based on last activity (simple heuristic)
  const now = Math.floor(Date.now() / 1000);
  const hourAgo = now - 3600;
  const dayAgo = now - 86400;

  let status: 'online' | 'away' | 'offline' = 'online';
  if (lastActivity < dayAgo) {
    status = 'offline';
  } else if (lastActivity < hourAgo) {
    status = 'away';
  }

  return (
    <div
      onClick={onClick}
      onDoubleClick={onClick}
      className={cn(
        'aim-buddy-item',
        isSelected && 'selected'
      )}
    >
      <BuddyIcon status={status} />
      <span className="truncate flex-1">{displayName}</span>
    </div>
  );
};

const BuddyItem = memo(BuddyItemComponent);

interface BuddyGroupProps {
  title: string;
  buddies: ConversationSummary[];
  selectedPubkey: string | null;
  onSelectBuddy: (pubkey: string) => void;
  defaultExpanded?: boolean;
}

function BuddyGroup({ title, buddies, selectedPubkey, onSelectBuddy, defaultExpanded = true }: BuddyGroupProps) {
  const [expanded, setExpanded] = useState(defaultExpanded);

  return (
    <div className="mb-1">
      <div
        onClick={() => setExpanded(!expanded)}
        className="flex items-center gap-1 px-2 py-0.5 cursor-pointer hover:bg-[hsl(var(--muted))] text-xs font-bold"
      >
        {expanded ? (
          <ChevronDown className="w-3 h-3" />
        ) : (
          <ChevronRight className="w-3 h-3" />
        )}
        <span>{title} ({buddies.length})</span>
      </div>
      {expanded && (
        <div className="ml-2">
          {buddies.map((buddy) => (
            <BuddyItem
              key={buddy.pubkey}
              pubkey={buddy.pubkey}
              isSelected={selectedPubkey === buddy.pubkey}
              onClick={() => onSelectBuddy(buddy.pubkey)}
              lastActivity={buddy.lastActivity}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export function BuddyList({ onSelectBuddy, selectedPubkey, onNewIM }: BuddyListProps) {
  const { user, metadata } = useCurrentUser();
  const { conversations } = useDMContext();
  const { logout } = useLoginActions();
  const [showStatusSelector, setShowStatusSelector] = useState(false);

  const screenName = metadata?.name || (user ? genUserName(user.pubkey) : 'Guest');

  // Categorize buddies
  const { activeBuddies, awayBuddies, offlineBuddies } = useMemo(() => {
    const now = Math.floor(Date.now() / 1000);
    const hourAgo = now - 3600;
    const dayAgo = now - 86400;

    const active: ConversationSummary[] = [];
    const away: ConversationSummary[] = [];
    const offline: ConversationSummary[] = [];

    conversations.forEach((conv) => {
      if (conv.lastActivity < dayAgo) {
        offline.push(conv);
      } else if (conv.lastActivity < hourAgo) {
        away.push(conv);
      } else {
        active.push(conv);
      }
    });

    return { activeBuddies: active, awayBuddies: away, offlineBuddies: offline };
  }, [conversations]);

  return (
    <AIMWindow
      title={`${screenName}'s Buddy List`}
      icon={<RunningManIcon className="w-4 h-4" />}
      className="h-full w-72"
      showMinimize={false}
    >
      {/* Menu Bar */}
      <div className="bg-[hsl(var(--muted))] border-b border-[hsl(var(--border))] px-1 py-0.5 text-xs flex gap-3">
        <button className="hover:underline">My AIM</button>
        <button className="hover:underline">People</button>
        <button className="hover:underline">Help</button>
      </div>

      {/* Toolbar */}
      <div className="bg-[hsl(var(--card))] border-b border-[hsl(var(--border))] p-1 flex gap-1">
        <button
          onClick={onNewIM}
          className="aim-button px-2 py-0.5 text-xs flex items-center gap-1"
          title="Send an IM"
        >
          <MessageSquare className="w-3 h-3" />
          <span>IM</span>
        </button>
        <button className="aim-button px-2 py-0.5 text-xs flex items-center gap-1" title="People">
          <Users className="w-3 h-3" />
          <span>People</span>
        </button>
      </div>

      {/* User Info */}
      <div className="bg-[hsl(var(--card))] border-b border-[hsl(var(--border))] p-2 flex items-center gap-2">
        <div className="w-10 h-10 bg-[hsl(var(--muted))] border-2 border-[hsl(var(--border))] flex items-center justify-center flex-shrink-0">
          {metadata?.picture ? (
            <img
              src={metadata.picture}
              alt={screenName}
              className="w-full h-full object-cover"
            />
          ) : (
            <User className="w-5 h-5 text-[hsl(var(--muted-foreground))]" />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-xs font-bold truncate">{screenName}</div>
          <StatusDisplay onClick={() => setShowStatusSelector(true)} />
        </div>
      </div>

      {/* Status Selector Modal */}
      {showStatusSelector && (
        <StatusSelector onClose={() => setShowStatusSelector(false)} />
      )}

      {/* Buddy List */}
      <div className="flex-1 bg-white dark:bg-[hsl(220,15%,12%)] overflow-y-auto aim-window-inset m-1">
        {conversations.length === 0 ? (
          <div className="p-4 text-center text-xs text-[hsl(var(--muted-foreground))]">
            <p>No buddies yet</p>
            <p className="mt-1">Start a conversation to add buddies</p>
          </div>
        ) : (
          <>
            {activeBuddies.length > 0 && (
              <BuddyGroup
                title="Buddies"
                buddies={activeBuddies}
                selectedPubkey={selectedPubkey}
                onSelectBuddy={onSelectBuddy}
              />
            )}
            {awayBuddies.length > 0 && (
              <BuddyGroup
                title="Away"
                buddies={awayBuddies}
                selectedPubkey={selectedPubkey}
                onSelectBuddy={onSelectBuddy}
              />
            )}
            {offlineBuddies.length > 0 && (
              <BuddyGroup
                title="Offline"
                buddies={offlineBuddies}
                selectedPubkey={selectedPubkey}
                onSelectBuddy={onSelectBuddy}
                defaultExpanded={false}
              />
            )}
          </>
        )}
      </div>

      {/* Bottom Toolbar */}
      <div className="bg-[hsl(var(--card))] border-t border-[hsl(var(--border))] p-1 flex justify-between">
        <button className="aim-button px-2 py-0.5 text-xs flex items-center gap-1" title="Settings">
          <Settings className="w-3 h-3" />
        </button>
        <button
          onClick={logout}
          className="aim-button px-2 py-0.5 text-xs flex items-center gap-1"
          title="Sign Off"
        >
          <LogOut className="w-3 h-3" />
          <span>Sign Off</span>
        </button>
      </div>
    </AIMWindow>
  );
}
